import {
  DocumentOrientationEnum,
  DocumentTypeEnum,
  RequiredDocumentTypeEnum
} from "@repo/core/enums/proposals";
import type { NeedNewBiometryResponseDTO } from "@repo/core/services/entities/WayupRequisitions";
import type {
  PreProposalData,
  RequiredDocuments
} from "../store/preProposalStore";

const NEED_NEW_BIOMETRY_TRUE_VALUES = new Set(["S", "SIM", "TRUE", "1"]);
const NEED_NEW_BIOMETRY_FALSE_VALUES = new Set([
  "N",
  "NAO",
  "NÃO",
  "FALSE",
  "0"
]);

const normalizeDecisionValue = (value: unknown): string => {
  return String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
};

export const parseNeedNewBiometryValue = (
  value: unknown
): boolean | null => {
  const normalized = normalizeDecisionValue(value);

  if (NEED_NEW_BIOMETRY_TRUE_VALUES.has(normalized)) {
    return true;
  }

  if (NEED_NEW_BIOMETRY_FALSE_VALUES.has(normalized)) {
    return false;
  }

  return null;
};

const getProcessId = (value: unknown): string | undefined => {
  const processId = String(value ?? "").trim();
  return processId || undefined;
};

export const forceNewBiometryPatch = (): Partial<PreProposalData> => ({
  shouldForceNewBiometry: true,
  biometryReuseProcessId: undefined,
  strIdProcessoUnico: undefined
});

export const resolveNeedNewBiometryPatch = (
  response?: Partial<NeedNewBiometryResponseDTO> | null
): Partial<PreProposalData> => {
  const needsNewBiometry = parseNeedNewBiometryValue(
    response?.necessidade_nova_bio
  );
  const processId = getProcessId(response?.processoid);

  if (needsNewBiometry === false && processId) {
    return {
      shouldForceNewBiometry: false,
      biometryReuseProcessId: processId,
      strIdProcessoUnico: processId
    };
  }

  return forceNewBiometryPatch();
};

export const BIOMETRY_REQUIRED_DOCUMENT: RequiredDocuments = {
  strTipoDocumento: RequiredDocumentTypeEnum.BIOMETRY,
  strDescricao: "Biometria",
  strNumeroPaginas: 1,
  strOrientacaoPagina: DocumentOrientationEnum.VERTICAL,
  isUploaded: false
};

export const filterBiometryUploadedDocumentTypes = (
  uploadedDocumentTypes: string[] = []
): string[] => {
  const biometryTypes = new Set<string>([
    RequiredDocumentTypeEnum.BIOMETRY,
    DocumentTypeEnum.BIOMETRY,
    DocumentTypeEnum.BIOMETRY_ID
  ]);

  return uploadedDocumentTypes.filter(
    (documentType) => !biometryTypes.has(String(documentType).trim())
  );
};

export const ensureRequiredBiometryDocument = (
  documents: RequiredDocuments[] = []
): RequiredDocuments[] => {
  let hasBiometryDocument = false;
  const nextDocuments = documents.map((document) => {
    if (
      String(document.strTipoDocumento).trim() !==
      RequiredDocumentTypeEnum.BIOMETRY
    ) {
      return document;
    }

    hasBiometryDocument = true;
    return {
      ...document,
      isUploaded: false
    };
  });

  if (hasBiometryDocument) {
    return nextDocuments;
  }

  return [...nextDocuments, BIOMETRY_REQUIRED_DOCUMENT];
};
